/* eslint-disable complexity */
import type { ValidationResult } from 'types';
import { ArgoUdfColumn, ArgoUdfColumnDataTypeEnum } from 'types/db';
export type UDFColumn = Pick<ArgoUdfColumn, 'id'
    | 'name'
    | 'name_unique'
    | 'enabled'
    | 'validation_pattern'
    | 'validation_unique'
    | 'validation_max'
    | 'validation_min'
    | 'validation_required'
    | 'data_type'
    | 'type'
    | 'description'>;
export type UDFData = {
    id: string;
    udf_column_id: string;
    str_val?: string | null;
    num_val?: number | null;
};

export function validateUdfData (data: Pick<UDFData, 'str_val' | 'num_val'>, def: UDFColumn): ValidationResult {
    const dataType = def.data_type;
    const required = def.validation_required;
    const min = def.validation_min;
    const max = def.validation_max;
    if (dataType === ArgoUdfColumnDataTypeEnum.Text) {
        if ((data.str_val === null || data.str_val === undefined) && required) {
            const message = `Required Property: ${def.name}`;
            return {
                status: 'error',
                errorMessage: message,
            };
        }
        if (typeof data.str_val !== 'string' && def.validation_required) {
            const message = `Incompatable input type for ${def.name}`;
            return {
                status: 'error',
                errorMessage: message,
            };
        }
        if (def.validation_pattern) {
            if (data.str_val === null || data.str_val === undefined) {
                const message = `Input for ${def.name} cannot be null or undefined when validation pattern is specified`;
                return {
                    status: 'error',
                    errorMessage: message,
                };
            }
            const regexp = new RegExp(`^${def.validation_pattern}$`, 'ug');
            if (!regexp.test(data.str_val)) {
                const message = `Input for ${def.name} doesnt pass validation ^${def.validation_pattern}$`;
                return {
                    status: 'error',
                    errorMessage: message,
                };
            }
        }
    } else if (dataType === ArgoUdfColumnDataTypeEnum.Number) {
        const numVal = data.num_val;
        if (typeof numVal !== 'number' && required) {
            const message = `Required Property: ${def.name}`;
            return {
                status: 'error',
                errorMessage: message,
            };
        }
        if (typeof numVal !== 'number' && def.validation_required) {
            const message = `Incompatable input type for ${def.name}`;
            return {
                status: 'error',
                errorMessage: message,
            };
        }
        if (typeof numVal === 'number') {
            if (isNaN(numVal)) {
                const message = `Input for ${def.name} is NaN (not a number)`;
                return {
                    status: 'error',
                    errorMessage: message,
                };
            }
            if (min && numVal < min) {
                const message = `Input for ${def.name} is lower than minumum: ${min}`;
                return {
                    status: 'error',
                    errorMessage: message,
                };
            }
            if (max && numVal > max) {
                const message = `Input for ${def.name} is higher than maximum: ${max}`;
                return {
                    status: 'error',
                    errorMessage: message,
                };
            }
        }
    }
    return {
        status: 'success',
    };
}
export function validateUdfDataArray (udfData: UDFData[], defs: UDFColumn[], opts?: { throwOnMissingColumn?: boolean, skipRequired?: boolean }): boolean {
    if (!opts?.skipRequired) {
        const requiredDefs = defs.filter(e => e.validation_required);
        if (!requiredDefs.every(def => udfData.some(e => e.udf_column_id === def.id))) {
            const requiredColumn = requiredDefs.find(def => !udfData.find(d => def.id === d.udf_column_id));
            if (opts?.throwOnMissingColumn) {
                throw new Error(`REQUIRED VALUE FOR FIELD ${requiredColumn?.name} ⚠`);
            }
            return false;
        }
    }
    return udfData.every(data => {
        const def = defs.find(e => e.id === data.udf_column_id);
        if (!def) {
            if (opts?.throwOnMissingColumn) {
                throw new Error('INVALID COLUMN ID FOR DATA ⚠');
            }
            return true;
        }
        return validateUdfData(data, def).status !== 'error';
    });
}
